import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button, Card, StatusPill, type Tone } from '@/design-system';
import { orderApi, orderKeys, type Order, type OrderService } from '@/entities/order';
import { CardTitleRow } from '@/shared/ui';

import { dossierCopy } from '../copy';

const copy = dossierCopy.services;

export interface ServicesCardProps {
    order: Order;
}

/**
 * Wat de klant besteld heeft, dienst voor dienst, en wanneer elke dienst klaar is.
 *
 * - **Een opname** is klaar als ze is ingediend; hier staat hoe ver ze is.
 * - **De NHG-bijlage** heeft geen opname: ze staat in het rapport van deze keuring.
 * - **Een dienst zonder vragenlijst**, zoals een energielabel, maak je buiten
 *   Smartkeur. Hij houdt het rapport niet tegen, maar staat open tot iemand hem
 *   klaar meldt. Wie dat mag, zegt de API per dienst (`can_complete`).
 */
export function ServicesCard({ order }: ServicesCardProps) {
    const client = useQueryClient();
    const [confirming, setConfirming] = useState<string | null>(null);
    const [done, setDone] = useState<string | null>(null);

    const complete = useMutation({
        mutationFn: (service: OrderService) =>
            orderApi.completeService(order.uuid, service.typeUuid ?? ''),
        onSuccess: (updated, service) => {
            setConfirming(null);
            setDone(copy.completed(service.name));
            client.setQueryData(orderKeys.detail(order.uuid), updated);
            void client.invalidateQueries({ queryKey: orderKeys.timeline(order.uuid) });
            void client.invalidateQueries({ queryKey: orderKeys.lists() });
        },
    });

    return (
        <Card clip>
            <CardTitleRow title={copy.title} />

            {done ? (
                <p
                    role="status"
                    className="m-0 border-b border-[var(--border-subtle-2)] px-4 py-[10px] text-[13px] leading-[1.5] text-[var(--accent-dark)]"
                >
                    {done}
                </p>
            ) : null}

            <ul className="m-0 list-none p-0">
                {order.services.map((service, index) => {
                    const stand = standOf(service, order);
                    const key = `${service.typeUuid ?? 'dienst'}-${index}`;
                    const asking = confirming === key;

                    return (
                        <li
                            key={key}
                            className="border-b border-[var(--border-subtle-2)] px-4 py-[13px] last:border-b-0"
                        >
                            <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                                <div className="min-w-0">
                                    <div className="text-[14px] leading-[1.35] font-medium text-[var(--text-primary)]">
                                        {service.name}
                                    </div>
                                    <div className="mt-[3px] text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                                        {stand.note}
                                    </div>
                                </div>
                                <StatusPill tone={stand.tone}>{stand.label}</StatusPill>
                            </div>

                            {service.canComplete && !asking ? (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="mt-[10px]"
                                    onClick={() => {
                                        complete.reset();
                                        setDone(null);
                                        setConfirming(key);
                                    }}
                                >
                                    {copy.complete}
                                </Button>
                            ) : null}

                            {service.canComplete && asking ? (
                                <div className="mt-[10px] flex flex-col gap-2">
                                    <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-primary)]">
                                        {copy.confirmQuestion(service.name)}
                                    </p>
                                    {complete.isError ? (
                                        <p
                                            role="alert"
                                            className="m-0 text-[13px] leading-[1.5] text-[var(--danger)]"
                                        >
                                            {copy.failed}
                                        </p>
                                    ) : null}
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            disabled={complete.isPending}
                                            onClick={() => complete.mutate(service)}
                                        >
                                            {complete.isPending ? copy.busy : copy.confirm}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={complete.isPending}
                                            onClick={() => setConfirming(null)}
                                        >
                                            {copy.keep}
                                        </Button>
                                    </div>
                                </div>
                            ) : null}
                        </li>
                    );
                })}
            </ul>
        </Card>
    );
}

/** Wat er bij een dienst staat: hoe hij klaar komt, en hoe ver hij is. */
function standOf(
    service: OrderService,
    order: Pick<Order, 'status'>,
): { label: string; tone: Tone; note: string } {
    if (service.status === 'cancelled' || order.status === 'cancelled') {
        return { label: copy.cancelled, tone: 'neutral', note: copy.notes[service.delivery] };
    }

    switch (service.delivery) {
        case 'report':
            return {
                label: copy.inReport,
                tone: service.status === 'completed' ? 'accent' : 'neutral',
                note: copy.notes.report,
            };
        case 'manual':
            return service.status === 'completed'
                ? { label: copy.done, tone: 'accent', note: copy.notes.manual }
                : { label: copy.open, tone: 'warn', note: copy.notes.manualOpen };
        default:
            return surveyStand(service, order);
    }
}

function surveyStand(
    service: OrderService,
    order: Pick<Order, 'status'>,
): { label: string; tone: Tone; note: string } {
    const inspection = service.inspection;

    if (!inspection) {
        return {
            label: ['new', 'confirmed'].includes(order.status)
                ? copy.surveyNotPlanned
                : copy.surveyNotReady,
            tone: 'neutral',
            note: copy.notes.survey,
        };
    }

    const tone: Tone =
        inspection.status === 'submitted'
            ? 'accent'
            : inspection.status === 'not_started'
              ? 'neutral'
              : 'warn';

    return { label: inspection.statusLabel, tone, note: copy.notes.survey };
}
